import { useCallback, useEffect, useMemo, useState } from "react";
import {
  NewPageWrapper,
  NewPageWrapperCopy,
} from "../../../../ui-components/custom-components";
import { PlatformUser } from "../list-users";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTrash } from "@fortawesome/free-solid-svg-icons";
import { useUser } from "../../../../contexts/user-context";
import { useEthosNotification } from "../../../../contexts/ethos-notification-context";

type ExportUsersModalProps = {
  users: PlatformUser[];
  isAdmin: boolean;
  closeModal: (value: boolean) => void;
};

const ROLE_ORDER: Record<string, number> = {
  ADMIN: 1,
  AGENTE: 2,
  SALES: 3,
  BROKER: 4,
  AVVOCATO: 5,
};

const filterOptions: { label: string; value: string }[] = [
  { label: "Pagati", value: "PAID" },
  { label: "Non Pagati", value: "NOT_PAID" },
  { label: "Tutti", value: "ALL" },
];

const capitalize = (str: string): string => {
  return str
    ? str
        .toLowerCase()
        .trim()
        .split(/\s+/)
        .map((word) => word[0].toUpperCase() + word.slice(1))
        .join(" ")
    : "";
};

const ExportUsersModal = ({
  users,
  isAdmin,
  closeModal,
}: ExportUsersModalProps) => {
  const { generateUsersReport, getTemplateUsers } = useUser();
  const { handleError, showErrorToast } = useEthosNotification();

  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [isExporting, setIsExporting] = useState(false);
  const [templatesOptions, setTemplatesOptions] = useState<
    { id: string; label: string; timestamp: number }[]
  >([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState("");
  const [selectedFilter, setSelectedFilter] = useState("");

  const [dateFrom, setDateFrom] = useState<string>("");
  const [dateTo, setDateTo] = useState<string>("");

  const allIds = useMemo(() => users.map((u) => u._id), [users]);

  const isAllSelected = useMemo(() => {
    return users.length > 0 && selectedIds.size === users.length;
  }, [users.length, selectedIds.size]);

  const rolesInUsers = useMemo(() => {
    const roles = Array.from(new Set(users.map((u) => u.role)));
    return roles.sort(
      (a, b) => (ROLE_ORDER[a] ?? 999) - (ROLE_ORDER[b] ?? 999),
    );
  }, [users]);

  const idsByRole = useMemo(() => {
    const map = new Map<string, string[]>();
    for (const u of users) {
      const arr = map.get(u.role) ?? [];
      arr.push(u._id);
      map.set(u.role, arr);
    }
    return map;
  }, [users]);

  const toggleRoleSelection = (role: string) => {
    const ids = idsByRole.get(role) ?? [];

    setSelectedIds((prev) => {
      const next = new Set(prev);

      const allRoleSelected = ids.length > 0 && ids.every((id) => next.has(id));

      if (allRoleSelected) {
        ids.forEach((id) => next.delete(id));
      } else {
        ids.forEach((id) => next.add(id));
      }

      return next;
    });
  };

  const sortedUsers = useMemo(() => {
    return [...users].sort((a, b) => {
      const roleA = ROLE_ORDER[a.role] ?? 999;
      const roleB = ROLE_ORDER[b.role] ?? 999;

      if (roleA !== roleB) return roleA - roleB;

      return a.last_name.localeCompare(b.last_name);
    });
  }, [users]);

  const toggleOne = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleAll = () => {
    setSelectedIds((prev) => {
      if (prev.size === users.length) return new Set();
      return new Set(allIds);
    });
  };

  const loadTemplates = useCallback(async () => {
    const { data: templatesData, error } = await getTemplateUsers();

    if (error) {
      handleError(error);
      return;
    }

    const toArray = [templatesData];

    const services = Array.isArray(toArray)
      ? toArray
      : (templatesData?.data ?? []);

    const activeTemplates = services
      .flatMap((service) => service?.templates ?? [])
      .filter((template) => template?.status === "ACTIVE" && template?.id)
      .sort((a, b) => {
        const aTime = a?.createdAt ? new Date(a.createdAt).getTime() : -1;
        const bTime = b?.createdAt ? new Date(b.createdAt).getTime() : -1;
        return bTime - aTime;
      });

    const options = activeTemplates.map((template) => {
      const name = template?.filename || "Template Compensi Utente";
      const dateValue = template?.createdAt;
      const timestamp = dateValue ? new Date(dateValue).getTime() : -1;
      const dateLabel =
        timestamp > 0
          ? new Date(dateValue).toLocaleDateString("it-IT")
          : "N.D.";
      return {
        id: template.id,
        label: `${name} - ${dateLabel}`,
        timestamp,
      };
    });
    setTemplatesOptions(options);
  }, [getTemplateUsers, handleError]);

  useEffect(() => {
    loadTemplates();
  }, [loadTemplates]);

  const downloadReportFile = (data: any) => {
    const url = data?.url;
    const filename = data?.filename || "report";
    if (!url) {
      showErrorToast("URL non disponibile per il download.");
      return;
    }

    const element = document.createElement("a");
    element.href = url;
    element.download = filename;
    document.body.appendChild(element);
    element.click();
    element.remove();
  };

  const exportSelected = async () => {
    try {
      setIsExporting(true);
      if (!selectedTemplateId) {
        showErrorToast("Seleziona un template per esportare.");
        return;
      }

      const body: {
        users: string[];
        startDate?: Date;
        endDate?: Date;
        paymentFilter?: string;
      } = { users: Array.from(selectedIds) };

      if (dateFrom) body.startDate = new Date(dateFrom);
      if (dateTo) body.endDate = new Date(dateTo);
      if (selectedFilter) body.paymentFilter = selectedFilter;

      const { data, error } = await generateUsersReport(
        selectedTemplateId,
        body,
      );
      if (error) {
        handleError(error);
        return;
      }

      downloadReportFile(data);
    } finally {
      setIsExporting(false);
    }
  };

  const hasInvalidRange = useMemo(() => {
    return Boolean(dateFrom && dateTo && dateFrom > dateTo);
  }, [dateFrom, dateTo]);

  return (
    <NewPageWrapper>
      <NewPageWrapperCopy>
        <div className="top-page">
          <h3 className="top-page-title">Esporta utenti</h3>

          <div className="d-flex gap-2">
            <button
              className="btn btn-secondary"
              disabled={isExporting}
              onClick={() => closeModal(false)}
            >
              Chiudi
            </button>
            <button
              className="btn btn-success"
              disabled={
                !isAdmin ||
                isExporting ||
                selectedIds.size === 0 ||
                hasInvalidRange ||
                !selectedTemplateId
              }
              onClick={exportSelected}
              title={hasInvalidRange ? "Intervallo date non valido" : undefined}
            >
              {isExporting
                ? "Esportazione..."
                : `Esporta (${selectedIds.size})`}
            </button>
          </div>
        </div>

        <div className="d-flex justify-content-between align-items-end my-3 gap-3 flex-wrap">
          <div className="d-flex align-items-end gap-2 flex-wrap">
            <div>
              <label className="form-label mb-1 small">Dal</label>
              <input
                type="date"
                className="form-control form-control-sm"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                disabled={isExporting}
              />
            </div>

            <div>
              <label className="form-label mb-1 small">Al</label>
              <input
                type="date"
                className="form-control form-control-sm"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                disabled={isExporting}
              />
            </div>

            <button
              type="button"
              className="btn btn-outline-danger btn-sm"
              disabled={isExporting || (!dateFrom && !dateTo)}
              title="Cancella filtro date"
              onClick={() => {
                setDateFrom("");
                setDateTo("");
              }}
            >
              <FontAwesomeIcon icon={faTrash} />
            </button>

            <select
              className="form-select form-select-sm"
              value={selectedTemplateId}
              onChange={(e) => setSelectedTemplateId(e.target.value)}
              disabled={isExporting}
            >
              <option value="">Seleziona template</option>
              {templatesOptions.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.label}
                </option>
              ))}
            </select>

            <select
              className="form-select form-select-sm"
              value={selectedFilter}
              onChange={(e) => setSelectedFilter(e.target.value)}
              disabled={isExporting}
            >
              <option value="">Seleziona un filto</option>
              {filterOptions.map((t) => (
                <option key={t.value} value={t.value}>
                  {t.label}
                </option>
              ))}
            </select>
          </div>

          <div
            className="btn-group"
            role="group"
            aria-label="Seleziona per ruolo"
          >
            {rolesInUsers.map((role) => {
              const ids = idsByRole.get(role) ?? [];
              const selectedCount = ids.filter((id) =>
                selectedIds.has(id),
              ).length;
              const isActive = ids.length > 0 && selectedCount === ids.length;

              return (
                <button
                  key={role}
                  type="button"
                  className={`btn btn-sm ${isActive ? "btn-primary" : "btn-outline-primary"}`}
                  disabled={isExporting || ids.length === 0}
                  onClick={() => toggleRoleSelection(role)}
                >
                  {capitalize(role)}
                  {selectedCount > 0 && ` (${selectedCount})`}
                </button>
              );
            })}

            <button
              type="button"
              className="btn btn-outline-primary btn-sm"
              disabled={users.length === 0 || isExporting}
              onClick={toggleAll}
            >
              {isAllSelected ? "Deseleziona tutti" : "Seleziona tutti"}
            </button>
          </div>
        </div>

        <table className="table table-striped">
          <thead className="thead-dark">
            <tr>
              <th style={{ width: "5%" }}></th>
              <th>Nome</th>
              <th>Cognome</th>
              <th>Email</th>
              <th>Ruolo</th>
            </tr>
          </thead>

          <tbody>
            {users.length === 0 && (
              <tr>
                <td colSpan={5} className="text-center py-4">
                  Nessun utente trovato.
                </td>
              </tr>
            )}

            {sortedUsers.map((u) => {
              const checked = selectedIds.has(u._id);
              return (
                <tr key={u._id}>
                  <td>
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => toggleOne(u._id)}
                      disabled={isExporting}
                    />
                  </td>
                  <td>{capitalize(u.first_name)}</td>
                  <td>{capitalize(u.last_name)}</td>
                  <td>{u.email}</td>
                  <td>{u.role}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </NewPageWrapperCopy>
    </NewPageWrapper>
  );
};

export default ExportUsersModal;
